/**
 * CO₂e estimate v1 for harvested parts.
 *
 * These are rough averages based on ecoinvent/ICE-range material factors —
 * NOT a certified LCA. Refrigerant GWP and end-of-life treatment are excluded.
 * Everything goes through estimatePartFootprint() so the tables can later be
 * swapped for Planet Price data without touching the rest of the app.
 */

export const TRANSPORT_KG_CO2E_PER_KG = 0.45; // new-part shipping: deep-sea Asia→Rotterdam + road + parcel/packaging
export const FALLBACK_FACTOR = 4.0;
export const FOOTPRINT_METHOD = "fixid-estimate-v1";

type FootprintEntry = { weightKg: number; factor: number };

const normalize = (s: string) => s.toLowerCase().trim().replace(/\s+/g, " ");

/** Keyed by normalized recommendation-sheet part name. */
const SHEET_PARTS: Record<string, FootprintEntry> = {
  "drain pump": { weightKg: 0.6, factor: 5.0 },
  "circulation / wash pump": { weightKg: 1.2, factor: 5.0 },
  "control board / pcb": { weightKg: 0.35, factor: 28 },
  "door seal / boot gasket": { weightKg: 1.0, factor: 3.5 },
  "drive motor": { weightKg: 4.5, factor: 6.0 },
  "drum bearings (bearing kit)": { weightKg: 0.8, factor: 3.5 },
  "drying fan motor": { weightKg: 1.2, factor: 5.5 },
  "heat pump compressor": { weightKg: 7.0, factor: 5.0 },
  "heat pump unit / sealed heat exchanger assembly (heat pump type)": { weightKg: 12.0, factor: 5.5 },
  "heating element": { weightKg: 0.35, factor: 5.0 },
  "heating element (resistive)": { weightKg: 0.6, factor: 5.0 },
  "condenser unit / heat exchanger (condenser type)": { weightKg: 1.5, factor: 4.5 },
  "water inlet valve": { weightKg: 0.25, factor: 4.5 },
  "3-way water inlet valve (wash + condensing water)": { weightKg: 0.35, factor: 4.5 },
};

/** Keyed by normalized spare_part_types name (sheet partCategory / legacy harvest component name). */
const PART_TYPES: Record<string, FootprintEntry> = {
  "motor": { weightKg: 4.5, factor: 6.0 },
  "pump": { weightKg: 0.6, factor: 5.0 },
  "pump & pump filter": { weightKg: 0.7, factor: 4.5 },
  "electronics": { weightKg: 0.35, factor: 28 },
  "inverter": { weightKg: 0.5, factor: 30 },
  "heating element": { weightKg: 0.4, factor: 5.0 },
  "thermostat": { weightKg: 0.05, factor: 6 },
  "door seal / boot seal": { weightKg: 1.0, factor: 3.5 },
  "door seal / door gasket": { weightKg: 0.6, factor: 3.5 },
  "seal / gasket": { weightKg: 0.1, factor: 3.5 },
  "drive belt": { weightKg: 0.15, factor: 3.5 },
  "belt (drive belt)": { weightKg: 0.15, factor: 3.5 },
  "hose": { weightKg: 0.4, factor: 3.0 },
  "drum-tub-bearing": { weightKg: 0.8, factor: 3.5 },
  "drum": { weightKg: 6.0, factor: 4.5 },
  "shock absorber": { weightKg: 0.8, factor: 2.8 },
  "carbon brushes": { weightKg: 0.05, factor: 4.0 },
  "door": { weightKg: 4.0, factor: 2.0 },
  "hinge": { weightKg: 0.15, factor: 2.5 },
  "housing / casing": { weightKg: 8.0, factor: 2.2 },
  "detergent drawer": { weightKg: 0.4, factor: 2.5 },
  "detergent dispenser": { weightKg: 0.4, factor: 2.5 },
  "filter": { weightKg: 0.15, factor: 2.5 },
  "lint filter": { weightKg: 0.15, factor: 2.5 },
  "water filter": { weightKg: 0.15, factor: 2.5 },
  "fan / impeller": { weightKg: 0.3, factor: 3.0 },
  "condenser / collection tray": { weightKg: 1.5, factor: 4.5 },
  "heat pump system": { weightKg: 12, factor: 5.5 },
  "pressure chamber (water level sensor)": { weightKg: 0.1, factor: 4.0 },
  "water inlet / water supply": { weightKg: 0.3, factor: 4.5 },
  "water management (valves, flow systems)": { weightKg: 0.3, factor: 4.5 },
  "water management / inlet assembly": { weightKg: 0.3, factor: 4.5 },
  "spray arm": { weightKg: 0.15, factor: 2.5 },
  "rack / basket (dish rack)": { weightKg: 2.5, factor: 2.3 },
  "cutlery basket": { weightKg: 0.3, factor: 2.5 },
  "rack wheel / basket roller": { weightKg: 0.02, factor: 2.5 },
  "salt reservoir / salt container": { weightKg: 0.3, factor: 2.5 },
  "glass shelf": { weightKg: 1.5, factor: 1.4 },
  "door shelf / door bin": { weightKg: 0.3, factor: 2.5 },
  "drawer / basket / tray": { weightKg: 1.0, factor: 2.5 },
  "grate / rack": { weightKg: 1.5, factor: 2.3 },
  "freezer compartment flap": { weightKg: 0.4, factor: 2.5 },
  "water drain / drip tray": { weightKg: 0.3, factor: 2.5 },
  "lighting (drum light)": { weightKg: 0.05, factor: 15 },
  "lighting (interior light)": { weightKg: 0.05, factor: 15 },
  "cooling box / cooler unit": { weightKg: 10, factor: 5.0 },
  "ice dispenser": { weightKg: 1.0, factor: 3.5 },
  "water dispenser": { weightKg: 1.0, factor: 3.5 },
  "gas burner (cooling system component)": { weightKg: 0.5, factor: 3.0 },
  "accessories & maintenance": { weightKg: 0.2, factor: 2.5 },
};

export type FootprintMatch = "sheet" | "type" | "fallback";

export interface PartFootprint {
  weightKg: number;
  productionKg: number;
  transportKg: number;
  totalKg: number;
  method: typeof FOOTPRINT_METHOD;
  matchedOn: FootprintMatch;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function estimatePartFootprint({
  name,
  partType,
  weightKg,
}: {
  name?: string;
  partType?: string | null;
  weightKg?: number;
}): PartFootprint {
  const normName = name ? normalize(name) : "";
  const normType = partType ? normalize(partType) : "";

  let entry: FootprintEntry | undefined;
  let matchedOn: FootprintMatch = "fallback";
  if (normName && SHEET_PARTS[normName]) {
    entry = SHEET_PARTS[normName];
    matchedOn = "sheet";
  } else if (normType && PART_TYPES[normType]) {
    entry = PART_TYPES[normType];
    matchedOn = "type";
  } else if (normName && PART_TYPES[normName]) {
    entry = PART_TYPES[normName];
    matchedOn = "type";
  }

  // A passed weight is only used on the fallback path; table weights win.
  const weight = entry ? entry.weightKg : weightKg && weightKg > 0 ? weightKg : 0;
  const factor = entry ? entry.factor : FALLBACK_FACTOR;

  const productionKg = round1(weight * factor);
  const transportKg = round1(weight * TRANSPORT_KG_CO2E_PER_KG);
  return {
    // Weight stays unrounded so sums round correctly at display time.
    weightKg: weight,
    productionKg,
    transportKg,
    // Round the unrounded sum once so per-part totals add up consistently.
    totalKg: round1(weight * (factor + TRANSPORT_KG_CO2E_PER_KG)),
    method: FOOTPRINT_METHOD,
    matchedOn,
  };
}
